import React from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useUI } from '../../context/UIContext';
import {
    FiGrid, FiShoppingCart, FiPackage, FiUsers,
    FiBarChart2, FiSettings, FiLogOut, FiMapPin,
    FiClock, FiWifiOff
} from 'react-icons/fi';
import './Layout.css';

const Layout = () => {
    const { user, logout, isSuperAdmin, hasPermission } = useAuth();
    const { isOffline } = useUI();
    const navigate = useNavigate();

    const handleLogout = () => {
        if (isOffline) return;
        logout();
        navigate('/login');
    };

    const navItems = [
        { path: '/dashboard', icon: FiGrid, label: 'Dashboard', show: true },
        { path: '/pos', icon: FiShoppingCart, label: 'POS', show: hasPermission('pos') },
        { path: '/inventory', icon: FiPackage, label: 'Inventory', show: hasPermission('inventory') },
        { path: '/users', icon: FiUsers, label: 'Users', show: hasPermission('users') },
        { path: '/reports', icon: FiBarChart2, label: 'Reports', show: hasPermission('reports') },
        { path: '/branches', icon: FiMapPin, label: 'Branches', show: isSuperAdmin },
        { path: '/settings', icon: FiSettings, label: 'Settings', show: hasPermission('settings') },
    ];

    return (
        <div className="layout">
            {/* Sidebar */}
            <aside className="sidebar">
                <div className="sidebar-header">
                    <div className="logo">
                        <img src="/cravings_logo.png" alt="Cravings Logo" className="logo-img" />
                    </div>
                </div>

                {/* User Profile */}
                <div className="sidebar-profile">
                    <div className="profile-avatar">
                        {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0) || ''}
                    </div>
                    <div className="profile-info">
                        <div className="profile-name">{user?.firstName} {user?.lastName}</div>
                        <div className="profile-role">
                            {user?.role === 'super_admin' ? 'Super Admin' :
                                user?.role === 'branch_admin' ? 'Branch Admin' : 'Staff'}
                        </div>
                    </div>
                </div>

                {/* Branch Info */}
                {user?.branch && (
                    <div className="sidebar-branch">
                        <FiMapPin size={14} />
                        <span>{user.branch.name}</span>
                    </div>
                )}

                {/* Navigation */}
                <nav className="sidebar-nav">
                    {navItems.filter(item => item.show).map(item => (
                        <NavLink
                            key={item.path}
                            to={item.path}
                            className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                        >
                            <item.icon size={20} />
                            <span>{item.label}</span>
                        </NavLink>
                    ))}
                </nav>

                {/* Logout */}
                <div className="sidebar-footer">
                    <button
                        className="nav-item logout-btn"
                        onClick={handleLogout}
                        disabled={isOffline}
                        title={isOffline ? "Logout disabled while offline" : "Logout"}
                        style={isOffline ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                    >
                        {isOffline ? <FiWifiOff size={20} /> : <FiLogOut size={20} />}
                        <span>{isOffline ? 'Offline' : 'Logout'}</span>
                    </button>
                </div>
            </aside>

            {/* Main Content */}
            <main className="main-content">
                {/* Top Bar */}
                <header className="topbar">
                    <div className="topbar-left">
                        <h1 className="page-title">
                            {/* Will be set by each page */}
                        </h1>
                    </div>
                    <div className="topbar-right">
                        <div className="topbar-time">
                            <FiClock size={16} />
                            <span>{new Date().toLocaleDateString('en-US', {
                                weekday: 'short',
                                month: 'short',
                                day: 'numeric'
                            })}</span>
                        </div>
                    </div>
                </header>

                {/* Page Content */}
                <div className="page-content">
                    <Outlet />
                </div>
            </main>
        </div>
    );
};

export default Layout;
